'use client';

import { useState, Suspense } from 'react';
import { Box, Container, Typography, Button, Grid, Card, CardContent } from '@mui/material';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import PollIcon from '@mui/icons-material/Poll';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import GroupIcon from '@mui/icons-material/Group';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';
import dynamic from 'next/dynamic';

// Lazy load AuthDialog (optional but good practice when combined with Suspense)
const AuthDialog = dynamic(() => import('@/components/auth/AuthDialog'), {
  ssr: false,
});

export default function Home() {
  return (
    <Suspense fallback={null}>
      <HomeContent />
    </Suspense>
  );
}

function HomeContent() {
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const { isAuthenticated } = useAuth();
  const router = useRouter();

  return (
    <Box component="main">
      {/* Hero Section */}
      <Box 
        sx={{
          bgcolor: 'customBackground.main',
          py: { xs: 8, md: 12 },
          textAlign: 'center',
          color: 'white'
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h1"
            sx={{
              mb: 3,
              fontSize: { xs: '2rem', md: '2.5rem' }
            }}
          >
            Earn Rewards for Your Online Activities
          </Typography>
          <Typography
            variant="h2"
            sx={{
              mb: 4,
              color: 'white',
              fontSize: { xs: '1.25rem', md: '1.5rem' }
            }}
          >
            Are you looking for a fun and easy way to earn rewards in your spare time? Extensive Rewards offers you multiple opportunities to earn ER Points by engaging in simple online activities. Whether you enjoy sharing your opinions, exploring new offers, or connecting with a community, we've got something for you.
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            size="large"
            onClick={() => isAuthenticated ? router.push('/offerwalls') : setAuthDialogOpen(true)}
            sx={{ px: 4, py: 1.5 }}
          >
            Start Earning Now
          </Button>

          {/* Wrap AuthDialog in Suspense to support useSearchParams */}
          <Suspense fallback={null}>
            <AuthDialog
              open={authDialogOpen}
              onClose={() => setAuthDialogOpen(false)}
              initialMode="login"
            />
          </Suspense>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Grid container spacing={4}>
          {[{
            icon: <PollIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />,
            title: "Surveys",
            description: "Share your opinions and earn ER Points through our partner surveys"
          }, {
            icon: <MonetizationOnIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />,
            title: "Offerwalls",
            description: "Complete offers from our partners and earn additional rewards"
          }, {
            icon: <CardGiftcardIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />,
            title: "Gift Cards",
            description: "Redeem your ER Points for Amazon gift cards at great rates"
          }, {
            icon: <GroupIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />,
            title: "Referrals",
            description: "Invite friends and earn bonus points when they join and participate"
          }].map(({ icon, title, description }, idx) => (
            <Grid key={idx} size={{ xs:12, sm:6, md:3 }} sx={{ width: { xs: '80%', sm: 'auto' }, mx: { xs: 'auto', sm: 0 } }}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 2 }}>
                <CardContent>
                  {icon}
                  <Typography variant="h3" sx={{ mb: 1 }}>
                    {title}
                  </Typography>
                  <Typography color="text.secondary">
                    {description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
